import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type CostCenterResponse = {
  tenant_id: string;
  from: string;
  to: string;
  generated_at: string;
  plan: {
    plan_id: string;
    plan_name: string;
    price_brl_monthly: number | null;
  };
  revenue: {
    period_days: number;
    estimated_revenue_brl: number | null;
    basis: string;
  };
  usage: Record<string, number> & {
    human_assisted_cases: number;
    channel_volume: Record<string, number>;
  };
  ai: {
    ai_runs: number;
    runs_with_estimated_cost: number;
    input_tokens: number;
    output_tokens: number;
    estimated_cost_brl: number;
    by_provider: Record<string, CostBucket>;
    by_model: Record<string, CostBucket>;
  };
  costs: {
    ai_brl: number;
    messages_received_brl: number;
    messages_sent_brl: number;
    integrations_brl: number;
    flows_brl: number;
    human_assisted_brl: number;
    total_brl: number;
  };
  margin: {
    estimated_gross_margin_brl: number | null;
    estimated_gross_margin_rate: number | null;
    tenant_profitable: boolean | null;
  };
  data_quality: {
    metering_days: number;
    ai_runs_analyzed: number;
    ai_runs_capped: boolean;
    cost_rates_source: string;
    cost_is_estimate: boolean;
  };
};

type CostBucket = {
  ai_runs: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_brl: number;
};

function rangeDays(days: number) {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setUTCDate(toDate.getUTCDate() - (days - 1));
  return { from: fromDate.toISOString().slice(0, 10), to: toDate.toISOString().slice(0, 10) };
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR').format(value);
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function MetricCard(props: { label: string; value: string; tone?: 'alert' | 'warn' | undefined }) {
  return (
    <div className={['metric-card', props.tone ? `metric-card-${props.tone}` : ''].join(' ')}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

function sortedCostEntries(input: Record<string, CostBucket>) {
  return Object.entries(input).sort(
    (a, b) => b[1].estimated_cost_brl - a[1].estimated_cost_brl || a[0].localeCompare(b[0]),
  );
}

function CostBucketTable(props: { title: string; items: Record<string, CostBucket> }) {
  const entries = sortedCostEntries(props.items);
  return (
    <section className="panel metric-table metric-detail-table">
      <h2>{props.title}</h2>
      {entries.length ? (
        <table>
          <thead>
            <tr>
              <th>Dimensao</th>
              <th>Runs</th>
              <th>Tokens entrada</th>
              <th>Tokens saida</th>
              <th>Custo</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([label, item]) => (
              <tr key={label}>
                <th>{label}</th>
                <td className="numeric">{formatNumber(item.ai_runs)}</td>
                <td className="numeric">{formatNumber(item.input_tokens)}</td>
                <td className="numeric">{formatNumber(item.output_tokens)}</td>
                <td className="numeric">{formatMoney(item.estimated_cost_brl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">Sem execucoes de IA no periodo.</p>
      )}
    </section>
  );
}

function CostBreakdownTable(props: { data: CostCenterResponse }) {
  const rows = [
    ['IA', props.data.costs.ai_brl],
    ['Mensagens recebidas', props.data.costs.messages_received_brl],
    ['Mensagens enviadas', props.data.costs.messages_sent_brl],
    ['Integracoes', props.data.costs.integrations_brl],
    ['Flows', props.data.costs.flows_brl],
    ['Atendimento humano', props.data.costs.human_assisted_brl],
  ] as const;

  return (
    <section className="panel metric-table metric-detail-table">
      <h2>Custos variaveis</h2>
      <table>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th>{label}</th>
              <td className="numeric">{formatMoney(value)}</td>
            </tr>
          ))}
          <tr>
            <th>Total</th>
            <td className="numeric">{formatMoney(props.data.costs.total_brl)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function UsageTable(props: { data: CostCenterResponse }) {
  const rows = [
    ['Mensagens recebidas', props.data.usage.messages_received],
    ['Mensagens enviadas', props.data.usage.messages_sent],
    ['Casos criados', props.data.usage.cases_created],
    ['Casos encerrados', props.data.usage.cases_closed],
    ['Chamadas de IA', props.data.usage.ai_calls],
    ['Flows usados', props.data.usage.flows_used],
    ['Usuarios ativos', props.data.usage.active_users],
    ['Integracoes acionadas', props.data.usage.integrations_triggered],
    ['Atendimentos humanos', props.data.usage.human_assisted_cases],
  ] as const;

  return (
    <section className="panel metric-table metric-detail-table">
      <h2>Consumo</h2>
      <table>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th>{label}</th>
              <td className="numeric">{formatNumber(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function CostCenterPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [periodDays, setPeriodDays] = useState(30);
  const [data, setData] = useState<CostCenterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!tenant_id || !hasScope('METRICS_VIEW')) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range = rangeDays(periodDays);
    request<CostCenterResponse>(
      `/tenants/${encodeURIComponent(tenant_id)}/cost-center?from=${range.from}&to=${range.to}`,
    )
      .then((out) => {
        if (!cancelled) setData(out);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err instanceof Error ? err.message : err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasScope, periodDays, request, tenant_id]);

  useEffect(() => {
    return load();
  }, [load]);

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!hasScope('METRICS_VIEW')) return <main className="page">Sem permissao para custos.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Cost Center</h1>
          <p>Tenant {tenant_id}</p>
        </div>
        <div className="header-actions">
          <div className="segmented-control" aria-label="Periodo">
            <button
              type="button"
              className={periodDays === 7 ? 'active' : ''}
              onClick={() => setPeriodDays(7)}
            >
              7 dias
            </button>
            <button
              type="button"
              className={periodDays === 30 ? 'active' : ''}
              onClick={() => setPeriodDays(30)}
            >
              30 dias
            </button>
          </div>
          <Link className="nav-link" to="/metrics">
            Metricas
          </Link>
          <Link className="nav-link" to="/operations">
            Operacao
          </Link>
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      {error ? <p className="error">{error}</p> : null}
      {loading && !data ? <p className="hint">Carregando custos...</p> : null}

      {data ? (
        <>
          <section className="metric-grid">
            <MetricCard label="Plano" value={data.plan.plan_name} />
            <MetricCard
              label="Receita estimada"
              value={formatMoney(data.revenue.estimated_revenue_brl)}
            />
            <MetricCard label="Custo variavel" value={formatMoney(data.costs.total_brl)} />
            <MetricCard
              label="Margem estimada"
              value={formatMoney(data.margin.estimated_gross_margin_brl)}
              tone={data.margin.tenant_profitable === false ? 'alert' : undefined}
            />
            <MetricCard
              label="Margem %"
              value={formatPercent(data.margin.estimated_gross_margin_rate)}
            />
            <MetricCard label="Custo IA" value={formatMoney(data.ai.estimated_cost_brl)} />
            <MetricCard label="Tokens entrada" value={formatNumber(data.ai.input_tokens)} />
            <MetricCard label="Tokens saida" value={formatNumber(data.ai.output_tokens)} />
          </section>

          <section className="metric-meta">
            <span>{data.from}</span>
            <span>{data.to}</span>
            <span>{data.revenue.period_days} dias</span>
            <span>{new Date(data.generated_at).toLocaleString('pt-BR')}</span>
          </section>

          <div className="metric-columns">
            <UsageTable data={data} />
            <CostBreakdownTable data={data} />
            <CostBucketTable title="IA por provider" items={data.ai.by_provider} />
            <CostBucketTable title="IA por modelo" items={data.ai.by_model} />
          </div>

          <section className="panel metric-table metric-detail-table metric-table-wide">
            <h2>Qualidade da estimativa</h2>
            <table>
              <tbody>
                <tr>
                  <th>Dias com metering</th>
                  <td className="numeric">{formatNumber(data.data_quality.metering_days)}</td>
                </tr>
                <tr>
                  <th>AI Runs analisados</th>
                  <td className="numeric">{formatNumber(data.data_quality.ai_runs_analyzed)}</td>
                </tr>
                <tr>
                  <th>AI Runs com custo</th>
                  <td className="numeric">{formatNumber(data.ai.runs_with_estimated_cost)}</td>
                </tr>
                <tr>
                  <th>Amostra limitada</th>
                  <td>{data.data_quality.ai_runs_capped ? 'Sim' : 'Nao'}</td>
                </tr>
                <tr>
                  <th>Base</th>
                  <td>{data.data_quality.cost_rates_source}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </>
      ) : null}
    </main>
  );
}
